import { calculateSha256Hash } from '../crypto';
import { isLeftResult, isRightResult } from '../../lib/trpcTypeGuards';
import { uploadArrayBufferWithProgress } from '../uploadHelpers';
import { BaseProvider } from './BaseProvider';
import type { ProviderConfig, UploadOptions, UploadResult } from './types';

type R2UploadFileType = NonNullable<
  NonNullable<UploadOptions['context']>['nftFileType']
>;
type R2UploadUrlResponse = {
  uploadUrl: string;
  stagingKey: string;
  key?: string;
  providerId?: string;
  publicUrl: string;
};

function isR2UploadUrlResponse(value: unknown): value is R2UploadUrlResponse {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.uploadUrl === 'string' &&
    typeof candidate.stagingKey === 'string' &&
    (typeof candidate.providerId === 'string' ||
      typeof candidate.key === 'string') &&
    typeof candidate.publicUrl === 'string'
  );
}

function unwrapR2UploadUrlResponse(value: unknown): R2UploadUrlResponse | null {
  if (isR2UploadUrlResponse(value)) {
    return value;
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;

  if ('right' in candidate) {
    const unwrapped = unwrapR2UploadUrlResponse(candidate.right);
    if (unwrapped) {
      return unwrapped;
    }
  }

  if ('result' in candidate) {
    const unwrapped = unwrapR2UploadUrlResponse(candidate.result);
    if (unwrapped) {
      return unwrapped;
    }
  }

  if ('data' in candidate) {
    const unwrapped = unwrapR2UploadUrlResponse(candidate.data);
    if (unwrapped) {
      return unwrapped;
    }
  }

  if ('json' in candidate) {
    const unwrapped = unwrapR2UploadUrlResponse(candidate.json);
    if (unwrapped) {
      return unwrapped;
    }
  }

  return null;
}

function extractR2UploadUrlError(value: unknown): string | null {
  if (!value || typeof value !== 'object') {
    return null;
  }

  const candidate = value as Record<string, unknown>;

  if (typeof candidate.message === 'string' && candidate.message) {
    return candidate.message;
  }

  if ('left' in candidate) {
    return extractR2UploadUrlError(candidate.left);
  }

  if ('error' in candidate) {
    return extractR2UploadUrlError(candidate.error);
  }

  if ('result' in candidate) {
    return extractR2UploadUrlError(candidate.result);
  }

  if ('data' in candidate) {
    return extractR2UploadUrlError(candidate.data);
  }

  return null;
}

export class R2Provider extends BaseProvider {
  name: 'r2' = 'r2';
  private trpcClient: ProviderConfig['trpcClient'] | null = null;

  async initialize(config?: ProviderConfig): Promise<void> {
    this.trpcClient = config?.trpcClient ?? null;

    if (!this.trpcClient) {
      throw new Error('tRPC client is required for Cloudflare R2 uploads');
    }
  }

  async uploadFromBuffer(
    buffer: Buffer,
    options?: UploadOptions,
  ): Promise<UploadResult> {
    if (!this.trpcClient) {
      throw new Error('Cloudflare R2 provider not initialized');
    }

    const contentType = options?.contentType || 'application/octet-stream';
    const fileType = this.getUploadFileType(options);
    const fileHash = await calculateSha256Hash(buffer);
    const fileExtension = this.getExtensionFromContentType(contentType, {
      fallback: 'bin',
    });

    const uploadUrlResult = await this.trpcClient.r2.generateUploadUrl.mutate({
      fileHash,
      fileType,
      fileExtension,
      contentType,
    });

    if (isLeftResult(uploadUrlResult)) {
      throw new Error(uploadUrlResult.left.message);
    }

    const uploadUrlPayload = isRightResult(uploadUrlResult)
      ? uploadUrlResult.right
      : unwrapR2UploadUrlResponse(uploadUrlResult);

    if (!uploadUrlPayload) {
      throw new Error(
        extractR2UploadUrlError(uploadUrlResult) ||
          'Failed to generate Cloudflare R2 upload URL',
      );
    }

    const { uploadUrl, stagingKey } = uploadUrlPayload;

    if (options?.onProgress) {
      this.reportProgress(0, buffer.length, options.onProgress, {
        message: 'Starting Cloudflare R2 upload...',
      });
    }

    await uploadArrayBufferWithProgress(
      buffer,
      uploadUrl,
      options?.onProgress
        ? (percentage) => {
            const uploadedBytes = Math.round(
              (percentage / 100) * buffer.length,
            );
            this.reportProgress(
              uploadedBytes,
              buffer.length,
              options.onProgress!,
              {
                message: 'Uploading to Cloudflare R2...',
              },
            );
          }
        : undefined,
      contentType,
    );

    const finalizeResult = await this.trpcClient.r2.finalizeUpload.mutate({
      fileHash,
      fileType,
      fileExtension,
      contentType,
      stagingKey,
    });

    if (isLeftResult(finalizeResult)) {
      throw new Error(finalizeResult.left.message);
    }

    const { providerId, publicUrl } = finalizeResult.right;

    if (options?.onProgress) {
      this.reportProgress(buffer.length, buffer.length, options.onProgress, {
        message: 'Upload complete',
      });
    }

    return {
      id: providerId,
      url: publicUrl,
      timestamp: Date.now(),
      size: buffer.length,
    };
  }

  private getUploadFileType(options?: UploadOptions): R2UploadFileType {
    const fileType = options?.context?.nftFileType;
    if (!fileType) {
      throw new Error('Cloudflare R2 uploads require an NFT file type context');
    }

    return fileType;
  }
}
